import {
  getCookie,
  isConsentCategoryEnabled,
  OneTrustCategories,
} from '@susu/cookie'

import {
  MARKETING_CLICK_ID_COOKIE_FALLBACK,
  MARKETING_CLICK_ID_PARAMS,
  MARKETING_CLICK_IDS_EXPIRY_DAYS,
  type MarketingClickIdParam,
  type MarketingClickIds,
  STORAGE_KEY_MARKETING_CLICK_IDS,
  type StoredMarketingClickIds,
  URL_PARAM_ALIASES,
} from './types'

/**
 * Checks if the value is a valid stored marketing click IDs object.
 */
export function isStoredMarketingClickIds(
  value: unknown,
): value is StoredMarketingClickIds {
  if (value === null || typeof value !== 'object') return false
  const candidate = value as Record<string, unknown>
  return (
    typeof candidate.expiresAt === 'number' &&
    candidate.clickIds !== null &&
    typeof candidate.clickIds === 'object'
  )
}

/**
 * Parses gclid from _gcl_aw cookie (format: version.timestamp.gclid).
 */
export function getGclidFromCookie(): string | undefined {
  const value = getCookie('_gcl_aw')
  if (!value) return undefined
  const segment = value.split('.')[2]
  return segment && segment.length > 0 ? segment : undefined
}

/**
 * Reads a click ID from platform cookies using the fallback map.
 */
export function getClickIdFromCookie(
  param: MarketingClickIdParam,
): string | undefined {
  if (param === 'gclid') {
    return getGclidFromCookie()
  }
  const cookieName = MARKETING_CLICK_ID_COOKIE_FALLBACK[param]
  if (!cookieName) return undefined
  return getCookie(cookieName) ?? undefined
}

/**
 * Reads marketing click IDs from URL search params (including aliases e.g. ScCid for sccid).
 */
export function getMarketingClickIdsFromUrl(url: URL): MarketingClickIds {
  const result: MarketingClickIds = {}
  for (const param of MARKETING_CLICK_ID_PARAMS) {
    const aliases = URL_PARAM_ALIASES[param] ?? [param]
    for (const name of aliases) {
      const value = url.searchParams.get(name)
      if (value) {
        result[param] = value
        break
      }
    }
  }
  return result
}

/**
 * Persists URL-sourced marketing click IDs into localStorage with a 30-day rolling expiry.
 *
 * - **URL has no click ID params:** no-op (no read/write of localStorage).
 * - **URL has params:** merges into any existing non-expired entry — only keys present in the URL
 *   are overwritten; all other stored keys are preserved. `expiresAt` is reset to now + 30 days.
 * - **Stored entry expired or invalid:** treated as an empty base; merged payload is URL keys only
 *   (existing expired blob is replaced in one write when the URL carries params).
 *
 * Writes only when OneTrust marketing / advertising consent (C0004) is granted.
 */
export function persistMarketingClickIdsToStorage(
  clickIdsFromUrl: MarketingClickIds,
): void {
  if (Object.keys(clickIdsFromUrl).length === 0) return
  if (typeof window === 'undefined') return
  if (!isConsentCategoryEnabled(OneTrustCategories.ADVERTISING)) return

  const expiresAt =
    Date.now() + MARKETING_CLICK_IDS_EXPIRY_DAYS * 24 * 60 * 60 * 1000
  let baseClickIds: MarketingClickIds = {}

  try {
    const raw = window.localStorage.getItem(STORAGE_KEY_MARKETING_CLICK_IDS)
    if (raw) {
      const parsed: unknown = JSON.parse(raw)
      if (
        isStoredMarketingClickIds(parsed) &&
        parsed.expiresAt > Date.now() &&
        parsed.clickIds &&
        typeof parsed.clickIds === 'object'
      ) {
        baseClickIds = { ...parsed.clickIds }
      }
    }
  } catch {
    // ignore invalid JSON; merge from URL only
  }

  const merged: MarketingClickIds = { ...baseClickIds, ...clickIdsFromUrl }

  try {
    window.localStorage.setItem(
      STORAGE_KEY_MARKETING_CLICK_IDS,
      JSON.stringify({ clickIds: merged, expiresAt }),
    )
  } catch {
    // localStorage full or unavailable
  }
}

/**
 * Reads marketing click IDs from localStorage. If the entry is expired, removes it and returns {}.
 * Returns {} when not in browser, when C0004 is not granted, or when missing/invalid.
 */
export function getMarketingClickIdsFromStorage(): MarketingClickIds {
  if (typeof window === 'undefined') return {}
  if (!isConsentCategoryEnabled(OneTrustCategories.ADVERTISING)) return {}
  try {
    const stored = window.localStorage.getItem(STORAGE_KEY_MARKETING_CLICK_IDS)
    if (!stored) return {}
    const parsed: unknown = JSON.parse(stored)
    if (!isStoredMarketingClickIds(parsed) || !parsed.clickIds) {
      return {}
    }
    if (parsed.expiresAt <= Date.now()) {
      try {
        window.localStorage.removeItem(STORAGE_KEY_MARKETING_CLICK_IDS)
      } catch {
        // ignore
      }
      return {}
    }
    return parsed.clickIds
  } catch {
    // invalid or missing stored value
  }
  return {}
}

/**
 * Reads all marketing click IDs from platform cookies (using cookie fallback map).
 */
export function getMarketingClickIdsFromCookies(): MarketingClickIds {
  const result: MarketingClickIds = {}
  for (const param of MARKETING_CLICK_ID_PARAMS) {
    const value = getClickIdFromCookie(param)
    if (value) {
      result[param] = value
    }
  }
  return result
}
